import { ref, get, set, update } from 'firebase/database';
import { database } from '../firebase';

// Production URL - all referral links should point to production
const PRODUCTION_URL = 'https://students.fosterway.com';

// Generate a random short code
export function generateShortCode(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Check if a short code already exists in the database
export async function isCodeUnique(code: string): Promise<boolean> {
  try {
    const snapshot = await get(ref(database, `referral_links/${code}`));
    return !snapshot.exists();
  } catch (error) {
    console.error('Error checking code uniqueness:', error);
    return false;
  }
}

// Generate a unique short code
export async function generateUniqueShortCode(): Promise<string> {
  let shortCode: string;
  let attempts = 0;
  const maxAttempts = 10;

  do {
    shortCode = generateShortCode();
    attempts++;
    
    if (attempts > maxAttempts) {
      throw new Error('Unable to generate unique short code after maximum attempts');
    }
  } while (!(await isCodeUnique(shortCode)));

  return shortCode;
}

// Get or create a referral link for a user
export async function getOrCreateReferralLink(userId: string): Promise<string> {
  try {
    // Always use production URL for referral links
    const originalUrl = `${PRODUCTION_URL}/login?ref=${userId}&signup=true`;
    
    // First, check if user already has a referral link
    const existingLinks = await getUserReferralLinks(userId);
    if (existingLinks.length > 0) {
      const existingLink = existingLinks[0];
      
      // Check if the original URL needs to be updated to production
      if (existingLink.originalUrl !== originalUrl) {
        // Update the original URL to point to production
        const snapshot = await get(ref(database, 'referral_links'));
        const allLinks = snapshot.val() || {};
        
        // Find the link data and update it
        for (const [code, data] of Object.entries(allLinks)) {
          if ((data as any).userId === userId) {
            await update(ref(database, `referral_links/${code}`), {
              originalUrl: originalUrl
            });
            break;
          }
        }
      }
      
      // Return the existing link with production URL
      return existingLink.shortUrl;
    }
    
    // No existing link found, create a new one
    const shortCode = await generateUniqueShortCode();
    
    const linkData = {
      userId,
      originalUrl,
      createdAt: Date.now(),
      clickCount: 0,
      lastClicked: null,
      isActive: true
    };
    
    await set(ref(database, `referral_links/${shortCode}`), linkData);
    
    // Always return production URL for QR codes
    return `${PRODUCTION_URL}/r/${shortCode}`;
  } catch (error) {
    console.error('Error getting or creating referral link:', error);
    throw error;
  }
}

// Create a shortened referral link (legacy function for backward compatibility)
export async function createReferralLink(userId: string): Promise<string> {
  return getOrCreateReferralLink(userId);
}

// Get referral link data by short code
export async function getReferralLinkData(shortCode: string) {
  try {
    const snapshot = await get(ref(database, `referral_links/${shortCode}`));
    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error('Error getting referral link data:', error);
    return null;
  }
}

// Update click statistics for a referral link
export async function updateClickStats(shortCode: string): Promise<void> {
  try {
    const linkData = await getReferralLinkData(shortCode);
    if (linkData) {
      await update(ref(database, `referral_links/${shortCode}`), {
        clickCount: linkData.clickCount + 1,
        lastClicked: Date.now()
      });
    }
  } catch (error) {
    console.error('Error updating click stats:', error);
  }
}

// Get all referral links for a specific user
export async function getUserReferralLinks(userId: string) {
  try {
    const snapshot = await get(ref(database, 'referral_links'));
    const allLinks = snapshot.val() || {};
    
    // Always use production URL for referral links
    return Object.entries(allLinks)
      .filter(([_, data]: [string, any]) => data.userId === userId)
      .map(([code, data]: [string, any]) => ({
        shortCode: code,
        shortUrl: `${PRODUCTION_URL}/r/${code}`,
        originalUrl: data.originalUrl,
        clickCount: data.clickCount,
        createdAt: data.createdAt,
        lastClicked: data.lastClicked,
        isActive: data.isActive
      }));
  } catch (error) {
    console.error('Error getting user referral links:', error);
    return [];
  }
}
